import { Entry } from 'contentful';
import ContentfulService from '../base/ContentfulService';
import ICustomSlide from '../../types/presentations/presentation/ICustomSlide';
import SlideType from '../../types/presentations/SlideType';

export default class CustomSlideService extends ContentfulService {
	/**
	 * Get an existing custom slide from contentful
	 * @param id
	 */
	public async get(id: string): Promise<ICustomSlide> {
		try {
			const entry: Entry<any> = await this.deliveryClient.getEntry(id);

			return this.parse(entry);
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Custom slide entry with id '${id}' could not be retrieved as it does not exist.`
			);
			throw error;
		}
	}

	public async update(customSlide: ICustomSlide): Promise<ICustomSlide> {
		const managementEnvironment: any = await this.getManagementEnvironment();

		try {
			let entry: Record<string, any> = await managementEnvironment.getEntry(customSlide.id);

			entry.fields.chapter = {
				de: {
					sys: {
						type: 'Link',
						linkType: 'Entry',
						id: customSlide.chapterId
					}
				}
			};
			entry.fields.headline = { de: customSlide.headline };
			entry.fields.copy = { de: customSlide.copy };
			entry.fields.badgeText = { de: customSlide.badgeText };
			entry.fields.asset = { de: customSlide.asset };

			entry = await entry.update();
			await entry.publish();
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Custom slide entry with id '${customSlide.id}' could not be updated as it does not exist.`
			);
			throw error;
		}

		return this.get(customSlide.id);
	}

	public async delete(customSlide: ICustomSlide): Promise<void> {
		const managementEnvironment: any = await this.getManagementEnvironment();

		let entry: Record<string, any> = await managementEnvironment.getEntry(customSlide.id);

		try {
			entry = await entry.unpublish();
			await entry.delete();
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Custom slide entry with id '${customSlide.id}' could not be deleted as it does not exist.`
			);
			throw error;
		}
	}

	public async create(customSlide: ICustomSlide): Promise<ICustomSlide> {
		const managementEnvironment: any = await this.getManagementEnvironment();

		let entry: Record<string, any> = await managementEnvironment.createEntry('customSlide', {
			fields: {
				chapter: {
					de: {
						sys: {
							linkType: 'Entry',
							id: customSlide.chapterId
						}
					}
				},
				headline: {
					de: customSlide.headline
				},
				copy: {
					de: customSlide.copy
				},
				badgeText: {
					de: customSlide.badgeText
				},
				asset: {
					de: customSlide.asset
				}
			}
		});

		entry = await entry.publish();

		return this.get(entry.sys.id);
	}

	private async parse(entry: Record<string, any>): Promise<ICustomSlide> {
		return {
			id: entry.sys.id,
			chapterId: entry.fields.chapter.sys.id,
			headline: entry.fields.headline,
			copy: entry.fields.copy,
			badgeText: entry.fields.badgeText,
			asset: entry.fields.asset,
			fields: {},
			type: SlideType.CustomSlide
		};
	}
}
